import { useMemo, useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type {
  FilterFieldDef,
  SearchFiltersSchema,
  SearchQuery,
} from '@/sources/types';
import { cn } from '@/lib/utils';

export interface FilterPanelProps {
  schema: SearchFiltersSchema;
  /** Current query (filters drive off this). Treated as initial state. */
  value: SearchQuery;
  /** Called on Apply with the merged query (keyword preserved from `value.q`). */
  onApply: (next: SearchQuery) => void;
  /** Called when the user clicks Clear (resets to empty). */
  onClear: () => void;
  /** Called when the user clicks Close / Cancel without applying. */
  onClose?: () => void;
  className?: string;
}

/**
 * Renders the source's filter schema as a form. The panel maintains its own
 * draft state internally so toggling chips doesn't refire the search; the
 * parent only sees an updated query when the user clicks Apply.
 *
 * The well-known field ids `sort`, `language`, `includeTags`, `excludeTags`
 * map to top-level `SearchQuery` fields. Everything else lands in
 * `q.extra[id]`.
 */
export function FilterPanel({
  schema,
  value,
  onApply,
  onClear,
  onClose,
  className,
}: FilterPanelProps) {
  const [draft, setDraft] = useState<SearchQuery>(value);
  const [tagDrafts, setTagDrafts] = useState<Record<string, string>>({});

  // If `value` changes externally (e.g. another filter applied via URL), we
  // could mirror it here — but keeping the draft local is the friendlier
  // UX (so toggling something then closing without Apply doesn't get reset
  // mid-edit). The parent can pass a `key` to force a remount if needed.

  function handleApply() {
    const next = commitPendingTagDrafts(schema, draft, tagDrafts);
    setTagDrafts({});
    onApply(next);
  }

  function handleClear() {
    setDraft({});
    setTagDrafts({});
    onClear();
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-md border border-border/60 bg-card/30 p-4',
        className,
      )}
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold tracking-tight">Filters</h2>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent/40 hover:text-foreground"
            aria-label="Close filters"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {schema.fields.map((field) => (
          <FieldEditor
            key={field.id}
            field={field}
            draft={draft}
            setDraft={setDraft}
            tagDrafts={tagDrafts}
            setTagDrafts={setTagDrafts}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" onClick={handleClear}>
          Clear
        </Button>
        <Button size="sm" onClick={handleApply}>
          Show results
        </Button>
      </div>
    </div>
  );
}

interface FieldEditorProps {
  field: FilterFieldDef;
  draft: SearchQuery;
  setDraft: React.Dispatch<React.SetStateAction<SearchQuery>>;
  tagDrafts: Record<string, string>;
  setTagDrafts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

function getFieldValue(field: FilterFieldDef, draft: SearchQuery): unknown {
  switch (field.id) {
    case 'sort':
      return draft.sort;
    case 'language':
      return draft.language;
    case 'includeTags':
      return draft.includeTags ?? [];
    case 'excludeTags':
      return draft.excludeTags ?? [];
    default:
      return draft.extra?.[field.id];
  }
}

function updateFieldValue(
  field: FilterFieldDef,
  draft: SearchQuery,
  next: unknown,
): SearchQuery {
  // Treat undefined / empty array / empty string as "unset".
  const isEmpty =
    next === undefined ||
    next === '' ||
    next === false ||
    (Array.isArray(next) && next.length === 0);

  switch (field.id) {
    case 'sort': {
      const out = { ...draft };
      if (isEmpty) delete out.sort;
      else out.sort = next as string;
      return out;
    }
    case 'language': {
      const out = { ...draft };
      if (isEmpty) delete out.language;
      else out.language = next as string;
      return out;
    }
    case 'includeTags': {
      const out = { ...draft };
      if (isEmpty) delete out.includeTags;
      else out.includeTags = next as string[];
      return out;
    }
    case 'excludeTags': {
      const out = { ...draft };
      if (isEmpty) delete out.excludeTags;
      else out.excludeTags = next as string[];
      return out;
    }
    default: {
      const extra = { ...(draft.extra ?? {}) };
      if (isEmpty) delete extra[field.id];
      else extra[field.id] = next;
      const out: SearchQuery = { ...draft };
      if (Object.keys(extra).length === 0) delete out.extra;
      else out.extra = extra;
      return out;
    }
  }
}

function splitTagDraft(raw: string): string[] {
  return raw
    .split(/[,\n]+/)
    .map((t) => t.trim())
    .filter(Boolean);
}

function mergeTagValues(current: string[], raw: string): string[] {
  const tags = splitTagDraft(raw);
  if (tags.length === 0) return current;

  const seen = new Set(current);
  const next = [...current];
  for (const tag of tags) {
    if (seen.has(tag)) continue;
    seen.add(tag);
    next.push(tag);
  }
  return next;
}

function commitPendingTagDrafts(
  schema: SearchFiltersSchema,
  query: SearchQuery,
  drafts: Record<string, string>,
): SearchQuery {
  let next = query;
  for (const field of schema.fields) {
    if (field.type !== 'tags') continue;
    const raw = drafts[field.id];
    if (!raw) continue;
    const current = getFieldValue(field, next);
    const merged = mergeTagValues(Array.isArray(current) ? (current as string[]) : [], raw);
    next = updateFieldValue(field, next, merged);
  }
  return next;
}

function FieldEditor({ field, draft, setDraft, tagDrafts, setTagDrafts }: FieldEditorProps) {
  const value = getFieldValue(field, draft);

  function commit(next: unknown) {
    setDraft((d) => updateFieldValue(field, d, next));
  }

  switch (field.type) {
    case 'select':
      return (
        <SelectField
          field={field}
          value={typeof value === 'string' ? value : ''}
          onChange={commit}
        />
      );
    case 'chips':
      return field.multi ? (
        <ChipsMulti
          field={field}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={commit}
        />
      ) : (
        <ChipsSingle
          field={field}
          value={typeof value === 'string' ? value : ''}
          onChange={commit}
        />
      );
    case 'number':
      return (
        <NumberField
          field={field}
          value={typeof value === 'number' ? value : undefined}
          onChange={commit}
        />
      );
    case 'toggle':
      return (
        <ToggleField field={field} value={value === true} onChange={commit} />
      );
    case 'tags':
      return (
        <TagsField
          field={field}
          value={Array.isArray(value) ? (value as string[]) : []}
          draft={tagDrafts[field.id] ?? ''}
          setDraft={(next) =>
            setTagDrafts((current) => {
              const out = { ...current };
              if (next === '') delete out[field.id];
              else out[field.id] = next;
              return out;
            })
          }
          onChange={commit}
        />
      );
    default:
      return null;
  }
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </label>
  );
}

interface SelectFieldProps {
  field: Extract<FilterFieldDef, { type: 'select' }>;
  value: string;
  onChange: (next: string | undefined) => void;
}
function SelectField({ field, value, onChange }: SelectFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{field.label}</FieldLabel>
      <select
        value={value}
        aria-label={field.label}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <option value="">Any</option>
        {field.options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface ChipsSingleProps {
  field: Extract<FilterFieldDef, { type: 'chips' }>;
  value: string;
  onChange: (next: string | undefined) => void;
}
function ChipsSingle({ field, value, onChange }: ChipsSingleProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>{field.label}</FieldLabel>
      <div className="flex flex-wrap gap-1.5">
        {field.options.map((opt) => {
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(active ? undefined : opt.value)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs',
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border/60 text-muted-foreground hover:bg-accent/30',
              )}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface ChipsMultiProps {
  field: Extract<FilterFieldDef, { type: 'chips' }>;
  value: string[];
  onChange: (next: string[] | undefined) => void;
}
function ChipsMulti({ field, value, onChange }: ChipsMultiProps) {
  const set = useMemo(() => new Set(value), [value]);
  function toggle(opt: string) {
    const next = new Set(set);
    if (next.has(opt)) next.delete(opt);
    else next.add(opt);
    const arr = [...next];
    onChange(arr.length === 0 ? undefined : arr);
  }
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>{field.label}</FieldLabel>
      <div className="flex flex-wrap gap-1.5">
        {field.options.map((opt) => {
          const active = set.has(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggle(opt.value)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs',
                active
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border/60 text-muted-foreground hover:bg-accent/30',
              )}
              aria-pressed={active}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface NumberFieldProps {
  field: Extract<FilterFieldDef, { type: 'number' }>;
  value: number | undefined;
  onChange: (next: number | undefined) => void;
}
function NumberField({ field, value, onChange }: NumberFieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <FieldLabel>{field.label}</FieldLabel>
      <Input
        type="number"
        value={value ?? ''}
        min={field.min}
        max={field.max}
        placeholder={field.placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') onChange(undefined);
          else {
            const n = Number(raw);
            onChange(Number.isFinite(n) ? n : undefined);
          }
        }}
        className="h-9"
      />
    </div>
  );
}

interface ToggleFieldProps {
  field: Extract<FilterFieldDef, { type: 'toggle' }>;
  value: boolean;
  onChange: (next: boolean | undefined) => void;
}
function ToggleField({ field, value, onChange }: ToggleFieldProps) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked ? true : undefined)}
        className="h-4 w-4 rounded border-input"
      />
      <span>{field.label}</span>
    </label>
  );
}

interface TagsFieldProps {
  field: Extract<FilterFieldDef, { type: 'tags' }>;
  value: string[];
  draft: string;
  setDraft: (next: string) => void;
  onChange: (next: string[] | undefined) => void;
}
function TagsField({ field, value, draft, setDraft, onChange }: TagsFieldProps) {
  function commit() {
    const next = mergeTagValues(value, draft);
    if (next.length === value.length && splitTagDraft(draft).length === 0) return;
    if (next.length !== value.length) onChange(next);
    setDraft('');
  }
  function remove(tag: string) {
    const next = value.filter((t) => t !== tag);
    onChange(next.length === 0 ? undefined : next);
  }
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel>{field.label}</FieldLabel>
      {value.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {value.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-accent/30 px-2 py-0.5 text-xs"
            >
              <span>{t}</span>
              <button
                type="button"
                onClick={() => remove(t)}
                className="rounded-full p-0.5 hover:bg-accent"
                aria-label={`Remove tag ${t}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
              const last = value[value.length - 1];
              if (last) remove(last);
            }
          }}
          placeholder={field.placeholder ?? 'tag:foo'}
          className="h-9 min-w-0 flex-1 px-2 text-sm"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={draft.trim() === ''}
          onPointerDown={(e) => e.preventDefault()}
          onClick={commit}
          className="h-9 shrink-0"
        >
          Add
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Tap Add for each tag, paste comma/newline-separated tags, or tap Show results to include
        the text in the box.
      </p>
    </div>
  );
}
